import type { AppStore } from '../core/store';
import type { VectorLayerModel } from '../core/types';
import { moveLayer, removeExternalLayer, updateLayer } from '../domain/layers';
import { emptySelectionSet } from '../domain/selectionSet';
import { clearWorkingLayer, replaceLayer } from '../domain/workingLayers';
import { layerHasCasurScopeKeys } from '../domain/displaySet';
import { icon, type IconName } from './icons';

const esc = (value: unknown): string => String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);

export interface LayerManagerActions {
  readonly zoom: (layerId: string) => void;
  readonly openTable?: (layerId: string) => void;
  readonly openStyle?: (layerId: string) => void;
  readonly exportLayer?: (layerId: string) => void;
}

type LayerAction = 'zoom' | 'table' | 'style' | 'rename' | 'up' | 'down' | 'lock' | 'clear' | 'export' | 'delete';

const glyphClass = (layer: VectorLayerModel): string => {
  if (layer.kind === 'master') return 'layer-glyph master';
  const types = layer.geometryTypes.join(' ');
  if (/Polygon/.test(types)) return 'layer-glyph';
  if (/LineString/.test(types)) return 'layer-glyph glyph-line';
  return 'layer-glyph glyph-point';
};
const kindLabel = (layer: VectorLayerModel): string => layer.kind === 'master' ? 'Maestro CASUR · solo lectura'
  : layer.kind === 'working' && layer.planning ? 'Objetos de planificación · puntos, líneas y polígonos'
  : layer.kind === 'working' ? `Capa de trabajo · ${layer.workingGeometryType === 'Polygon' ? 'polígonos' : layer.workingGeometryType === 'LineString' ? 'líneas' : 'puntos'}`
    : layer.kind === 'subarea' ? 'Áreas parciales' : `${layer.sourceFormat} · solo lectura`;

/**
 * Layer tree: visibility · glyph · name · "…" context menu. Configuration lives in the
 * "Estilo de capa" pane instead of forms expanded under every layer.
 */
export class LayerManager {
  private menu: HTMLElement | null = null;
  private styleLayerId: string | null = null;
  private readonly actions: LayerManagerActions;

  constructor(private readonly element: HTMLElement, private readonly store: AppStore, actions: LayerManagerActions | ((layerId: string) => void), private readonly stylePane?: HTMLElement) {
    this.actions = typeof actions === 'function' ? { zoom: actions } : actions;
    element.addEventListener('click', (event) => this.onClick(event));
    element.addEventListener('change', (event) => this.onChange(event));
    element.addEventListener('input', (event) => this.onInput(event));
    element.addEventListener('contextmenu', (event) => {
      const id = this.layerId(event.target as HTMLElement); if (!id) return;
      event.preventDefault(); this.openMenu(id, (event as MouseEvent).clientX, (event as MouseEvent).clientY);
    });
    stylePane?.addEventListener('change', (event) => this.onChange(event));
    stylePane?.addEventListener('input', (event) => this.onInput(event));
    stylePane?.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('pointerdown', (event) => { if (this.menu && !this.menu.contains(event.target as Node)) this.closeMenu(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeMenu(); });
  }

  render(layers: readonly VectorLayerModel[], activeLayerId: string | null): void {
    if (!layers.length) {
      this.element.innerHTML = '<div class="empty-layers">Importa un Maestro o una capa vectorial.</div>';
    } else {
      this.element.innerHTML = [...layers].reverse().map((layer) => {
        const locked = layer.kind === 'master' || layer.locked;
        return `<div class="layer-row ${layer.id === activeLayerId ? 'active' : ''}" data-layer-id="${esc(layer.id)}" role="treeitem" aria-selected="${layer.id === activeLayerId}">
          <input class="layer-visible" type="checkbox" ${layer.visible ? 'checked' : ''} aria-label="Visibilidad de ${esc(layer.name)}" />
          <span class="${glyphClass(layer)}"${layer.kind === 'master' ? '' : ` style="--glyph-stroke:${esc(layer.style.strokeColor)};--glyph-fill:${esc(layer.style.fillColor)}55"`}></span>
          <button class="layer-row-main layer-activate" type="button" title="${esc(layer.name)}"><span class="layer-row-name">${esc(layer.name)}</span><small>${esc(kindLabel(layer))} · ${layer.features.length.toLocaleString('es-NI')}${layer.warnings.length ? ` · ${layer.warnings.length} aviso(s)` : ''}</small></button>
          <span class="layer-row-tools">${locked ? `<span title="${layer.kind === 'master' ? 'Maestro protegido' : 'Capa bloqueada'}">${icon('lock')}</span>` : ''}<button type="button" class="layer-menu-button" data-layer-menu aria-haspopup="menu" aria-expanded="false" title="Acciones de capa" aria-label="Acciones de ${esc(layer.name)}">${icon('more')}</button></span>
        </div>`;
      }).join('');
    }
    if (this.styleLayerId) this.renderStylePane(this.styleLayerId);
  }

  /** Renders the "Estilo de capa" pane for one layer (opacity, colours, scope policy). */
  renderStylePane(layerId: string): void {
    this.styleLayerId = layerId;
    const pane = this.stylePane; if (!pane) return;
    const layer = this.store.getState().layers.find((item) => item.id === layerId);
    if (!layer) { pane.innerHTML = '<p class="pane-note">Elige una capa desde el panel Capas.</p>'; return; }
    // Do not rebuild controls while the user is dragging/typing in them.
    if (pane.dataset.layerId === layer.id && pane.contains(document.activeElement) && document.activeElement !== pane) return;
    pane.dataset.layerId = layer.id;
    const casurScope = layer.kind === 'generic' && layerHasCasurScopeKeys(layer);
    const editableName = layer.kind !== 'master' && layer.kind !== 'subarea';
    pane.innerHTML = `<div class="form-stack" data-layer-id="${esc(layer.id)}">
      <div class="pane-heading-row"><strong>${esc(layer.name)}</strong><small class="pane-note">${esc(kindLabel(layer))}</small></div>
      ${editableName ? `<label class="field"><span>Nombre</span><input class="layer-name-input" value="${esc(layer.name)}" maxlength="80" /></label>` : ''}
      <label class="field"><span>Opacidad · ${Math.round(layer.opacity * 100)}%</span><input class="layer-opacity" type="range" min="0" max="1" step="0.05" value="${layer.opacity}" /></label>
      ${layer.kind === 'master' ? '<button type="button" class="button" data-open-pane="style">Estilo de las entidades…</button>' : `<div class="field-grid"><label class="field"><span>Relleno</span><input class="layer-fill" type="color" value="${esc(layer.style.fillColor)}" /></label><label class="field"><span>Línea</span><input class="layer-stroke" type="color" value="${esc(layer.style.strokeColor)}" /></label><label class="field"><span>Grosor</span><input class="layer-width" type="range" min="0.5" max="8" step="0.5" value="${layer.style.strokeWidth}" /></label><label class="field"><span>Tamaño de punto</span><input class="layer-radius" type="range" min="2" max="16" step="1" value="${layer.style.pointRadius}" /></label></div>`}
      ${layer.kind === 'generic' ? `<label class="field"><span>Relación con el ámbito</span><select class="layer-scope-mode"><option value="none" ${layer.scopeMode === 'none' ? 'selected' : ''}>Mostrar siempre todo</option>${casurScope ? `<option value="attributes" ${layer.scopeMode === 'attributes' ? 'selected' : ''}>Seguir el ámbito (claves CASUR)</option>` : `<option value="spatial" ${layer.scopeMode === 'spatial' ? 'selected' : ''}>Filtrar espacialmente por el ámbito</option>`}</select></label>` : ''}
      ${layer.warnings.length ? `<div class="field-warning">${layer.warnings.map(esc).join('<br/>')}</div>` : ''}
      <small class="pane-note">${esc(layer.sourceName)} · ${esc(layer.sourceCrs)} · ${layer.features.length.toLocaleString('es-NI')} objetos · ${esc(layer.geometryTypes.join(', ') || 'sin geometría')}</small>
    </div>`;
  }

  private layerId(target: HTMLElement): string | undefined {
    return target.closest<HTMLElement>('[data-layer-id]')?.dataset.layerId;
  }

  private openMenu(layerId: string, x: number, y: number): void {
    this.closeMenu();
    const layer = this.store.getState().layers.find((item) => item.id === layerId); if (!layer) return;
    const protectedLayer = layer.kind === 'master';
    const managed = layer.kind === 'subarea';
    const item = (action: LayerAction, label: string, iconName: IconName, extra = ''): string => `<button type="button" role="menuitem" class="menu-item ${extra}" data-layer-action="${action}">${icon(iconName)}<span>${label}</span></button>`;
    const menu = document.createElement('div');
    menu.className = 'context-menu'; menu.setAttribute('role', 'menu'); menu.dataset.layerId = layerId;
    menu.innerHTML = [
      item('zoom', 'Zoom a la capa', 'target'),
      item('table', 'Abrir tabla', 'table'),
      item('style', 'Estilo…', 'palette'),
      protectedLayer || managed ? '' : item('rename', 'Renombrar…', 'pen'),
      '<div class="menu-separator"></div>',
      item('up', 'Subir', 'arrowUp'), item('down', 'Bajar', 'arrowDown'),
      protectedLayer || managed ? '' : item('lock', layer.locked ? 'Desbloquear' : 'Bloquear', layer.locked ? 'unlock' : 'lock'),
      layer.kind === 'working' ? item('clear', 'Vaciar objetos…', 'erase') : '',
      item('export', 'Exportar GeoJSON', 'download'),
      protectedLayer || managed ? '' : `<div class="menu-separator"></div>${item('delete', 'Eliminar capa…', 'trash', 'danger')}`,
    ].join('');
    menu.addEventListener('click', (event) => {
      const action = (event.target as Element).closest<HTMLElement>('[data-layer-action]')?.dataset.layerAction as LayerAction | undefined;
      if (action) { this.closeMenu(); this.runAction(layerId, action); }
    });
    document.body.append(menu);
    const rect = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(4, Math.min(x, window.innerWidth - rect.width - 8))}px`;
    menu.style.top = `${Math.max(4, Math.min(y, window.innerHeight - rect.height - 8))}px`;
    this.menu = menu;
    [...this.element.querySelectorAll<HTMLElement>('[data-layer-id]')].find((row) => row.dataset.layerId === layerId)?.querySelector('[data-layer-menu]')?.setAttribute('aria-expanded', 'true');
  }

  private closeMenu(): void {
    this.menu?.remove(); this.menu = null;
    this.element.querySelectorAll('[data-layer-menu][aria-expanded="true"]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
  }

  private onClick(event: Event): void {
    const target = event.target as HTMLElement;
    const id = this.layerId(target);
    if (!id) return;
    const menuButton = target.closest<HTMLElement>('[data-layer-menu]');
    if (menuButton) { const rect = menuButton.getBoundingClientRect(); this.openMenu(id, rect.left, rect.bottom + 2); return; }
    const action = target.closest<HTMLElement>('[data-layer-action]')?.dataset.layerAction as LayerAction | undefined;
    if (action) { this.runAction(id, action); return; }
    if (target.closest('.layer-activate')) this.activate(id);
  }

  private activate(id: string): void {
    const state = this.store.getState();
    const layer = state.layers.find((item) => item.id === id);
    const changedLayer = state.activeLayerId !== id;
    this.store.update({
      activeLayerId: id,
      genericSelection: changedLayer ? null : state.genericSelection,
      selection: changedLayer ? null : (layer?.kind === 'master' ? state.selection : null),
      selectionSet: changedLayer ? emptySelectionSet() : state.selectionSet,
      activeTool: changedLayer ? 'select' : state.activeTool,
    });
  }

  private runAction(id: string, action: LayerAction): void {
    const state = this.store.getState();
    const layer = state.layers.find((item) => item.id === id);
    if (!layer) return;
    if (action === 'zoom') { this.actions.zoom(id); return; }
    if (action === 'table') { if (state.activeLayerId !== id) this.activate(id); this.actions.openTable?.(id); return; }
    if (action === 'style') { this.renderStylePane(id); this.actions.openStyle?.(id); return; }
    if (action === 'export') { this.actions.exportLayer?.(id); return; }
    if (action === 'up' || action === 'down') { this.store.update({ layers: moveLayer(state.layers, id, action) }); return; }
    if (action === 'rename') {
      const name = window.prompt('Nombre de la capa', layer.name)?.trim();
      if (name) this.store.update({ layers: updateLayer(state.layers, id, { name }) });
      return;
    }
    if (action === 'delete') {
      if (layer.kind === 'master' || layer.kind === 'subarea') return;
      if (!window.confirm(layer.kind === 'working' && layer.features.length ? `Eliminar la capa de trabajo “${layer.name}” y sus ${layer.features.length} objeto(s)?` : `Eliminar la capa “${layer.name}” del proyecto?`)) return;
      const layers = removeExternalLayer(state.layers, id);
      const activeLayerId = state.activeLayerId === id ? layers.at(-1)?.id ?? null : state.activeLayerId;
      this.store.update({ layers, activeLayerId, activeTool: 'select', genericSelection: null, selection: null, selectionSet: emptySelectionSet() });
      return;
    }
    if (action === 'clear') {
      if (layer.kind !== 'working' || !layer.features.length) return;
      if (!window.confirm(`Eliminar los ${layer.features.length} objeto(s) de “${layer.name}”?`)) return;
      this.store.update({ layers: replaceLayer(state.layers, clearWorkingLayer(layer)), activeTool: 'select', genericSelection: null, selection: null, selectionSet: emptySelectionSet() });
      return;
    }
    if (action === 'lock' && layer.kind !== 'master') {
      this.store.update({
        layers: updateLayer(state.layers, id, { locked: !layer.locked }),
        activeTool: !layer.locked && state.activeLayerId === id ? 'select' : state.activeTool,
      });
    }
  }

  private onChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const id = this.layerId(target);
    if (!id) return;
    const layers = this.store.getState().layers;
    if (target.classList.contains('layer-visible')) this.store.update({ layers: updateLayer(layers, id, { visible: (target as HTMLInputElement).checked }) });
    if (target.classList.contains('layer-name-input')) this.store.update({ layers: updateLayer(layers, id, { name: target.value.trim() || 'Capa sin nombre' }) });
    if (target.classList.contains('layer-scope-mode')) this.store.update({ layers: updateLayer(layers, id, { scopeMode: target.value as VectorLayerModel['scopeMode'] }) });
  }

  private onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const id = this.layerId(target);
    if (!id) return;
    const state = this.store.getState();
    const layer = state.layers.find((item) => item.id === id);
    if (!layer) return;
    if (target.classList.contains('layer-opacity')) this.store.update({ layers: updateLayer(state.layers, id, { opacity: Number(target.value) }) });
    const styleKey = target.classList.contains('layer-fill') ? 'fillColor' : target.classList.contains('layer-stroke') ? 'strokeColor' : target.classList.contains('layer-width') ? 'strokeWidth' : target.classList.contains('layer-radius') ? 'pointRadius' : null;
    if (styleKey) this.store.update({ layers: updateLayer(state.layers, id, { style: { ...layer.style, [styleKey]: styleKey.endsWith('Color') ? target.value : Number(target.value) } }) });
  }
}
